import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { vacationRequestService } from '../../services/vacationRequestService';
import type { SellRequestStatus } from '../../models/vacation.types';
import { VACATION_APPROVALS_QUERY_KEY } from './usePendingApprovalsQuery';
import { VACATION_REQUESTS_QUERY_KEY } from './useMyVacationRequestsQuery';
import { VACATION_BALANCE_QUERY_KEY } from './useVacationBalanceQuery';
import { VACATION_DP_BOARD_QUERY_KEY } from './useVacationDpBoardQuery';
import { VACATION_HISTORY_DATA_QUERY_KEY } from './useVacationHistoryDataQuery';

export interface DecideRequestInput {
    requestId: string;
    kind: 'approve' | 'reject';
    note: string;
    managerEmail: string;
    sellStatus: SellRequestStatus;
    collaboratorEmail: string;
    collaboratorId: string;
    correctionRevision?: number;
}

export function useVacationApprovalMutations() {
    const queryClient = useQueryClient();
    const toast = useToast();

    const decideRequest = useMutation({
        retry: false,
        mutationFn: (input: DecideRequestInput) =>
            vacationRequestService.decide({
                requestId: input.requestId,
                kind: input.kind,
                note: input.note,
                managerEmail: input.managerEmail,
                sellStatus: input.sellStatus,
                correctionRevision: input.correctionRevision,
            }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [VACATION_APPROVALS_QUERY_KEY, variables.managerEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY, variables.collaboratorEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_BALANCE_QUERY_KEY, variables.collaboratorId],
            });
            queryClient.invalidateQueries({ queryKey: [VACATION_DP_BOARD_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [VACATION_HISTORY_DATA_QUERY_KEY] });

            const copy =
                variables.kind === 'reject'
                    ? {
                          title: 'Solicitação reprovada',
                          description:
                              'A reprovação foi registrada. O colaborador pode enviar uma nova solicitação.',
                      }
                    : {
                          title: 'Solicitação aprovada',
                          description: 'A aprovação foi registrada. O pedido segue para o DP.',
                      };

            toast.success(copy.title, copy.description);
        },
        onError: (error: Error) => {
            console.error('Erro ao decidir sobre solicitação de férias:', error);
            toast.error(
                'Não foi possível registrar a decisão',
                error.message || 'Verifique sua conexão e tente novamente.'
            );
        },
    });

    return { decideRequest };
}
